import { useState, useEffect, useCallback } from 'react';
import { useEasykitPlanningStore } from '@/stores/easykitPlanning';
import { isMarkerTask, EASYKIT_PROJECT_HOLIDAYS, EASYKIT_PROJECT_LOOSE } from '@/utils/easykit';
import { useEasykitTaskDisplay } from './useEasykitTaskDisplay';
import { EasykitScheduleOtherModal } from './EasykitScheduleOtherModal';
import type { OdooTask } from '@/types/odoo';

interface EasykitDetailHeaderProps {
  task: OdooTask;
}

/**
 * The detail panel's top bar: pill + editable prefix + headline + soName
 * + action buttons (Heractiveren / On hold / Verplaatsen / Verwijder uit
 * planning / Verwijderen / Sluiten). Owns the prefix-edit subform state.
 */
export function EasykitDetailHeader({ task }: EasykitDetailHeaderProps) {
  const selectTask = useEasykitPlanningStore((s) => s.selectTask);
  const updateTask = useEasykitPlanningStore((s) => s.updateTask);
  const enterMoveMode = useEasykitPlanningStore((s) => s.enterMoveMode);
  const unscheduleTask = useEasykitPlanningStore((s) => s.unscheduleTask);
  const deleteTask = useEasykitPlanningStore((s) => s.deleteTask);
  const setOnHold = useEasykitPlanningStore((s) => s.setOnHold);

  const { pill, primaryLabel: headlineLabel, soName } = useEasykitTaskDisplay(task);

  const initialPrefix = typeof task.x_studio_taak_prefix_1 === 'string' ? task.x_studio_taak_prefix_1.trim() : '';
  const [editingPrefix, setEditingPrefix] = useState(false);
  const [prefixDraft, setPrefixDraft] = useState(initialPrefix);
  const [savingPrefix, setSavingPrefix] = useState(false);
  const [scheduleOtherOpen, setScheduleOtherOpen] = useState(false);

  useEffect(() => {
    setPrefixDraft(initialPrefix);
    setEditingPrefix(false);
  }, [task.id, initialPrefix]);

  const handleSavePrefix = useCallback(async () => {
    const trimmed = prefixDraft.trim();
    if (trimmed === initialPrefix) { setEditingPrefix(false); return; }
    setSavingPrefix(true);
    try {
      await updateTask(task.id, { prefix: trimmed });
      setEditingPrefix(false);
    } catch {
      // Store sets error banner; keep editing open so user can retry.
    } finally {
      setSavingPrefix(false);
    }
  }, [task.id, prefixDraft, initialPrefix, updateTask]);

  // Visibility rules for action buttons.
  const isMarker = isMarkerTask(task.name);
  const isHolidayProject = task.project_id && task.project_id[0] === EASYKIT_PROJECT_HOLIDAYS;
  const isLooseTask = task.project_id && task.project_id[0] === EASYKIT_PROJECT_LOOSE;
  const isOnHold = task.x_studio_on_hold === true;
  const showHold = !isMarker && !isHolidayProject;
  const showMove = !isMarker && !isHolidayProject && !isOnHold;
  const showUnschedule = !isMarker && !isHolidayProject && !isLooseTask && !isOnHold;
  const showDelete = Boolean(isHolidayProject || isLooseTask);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
      <div className="min-w-0 pr-2 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          {pill && (
            <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${pill.cls}`}>
              {pill.label}
            </span>
          )}
          {editingPrefix ? (
            <div className="flex items-center gap-1.5 min-w-0">
              <input
                type="text"
                value={prefixDraft}
                onChange={(e) => setPrefixDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSavePrefix();
                  if (e.key === 'Escape') {
                    // Stop the global Escape handler in EasykitPlanningView from ALSO
                    // closing the panel and exiting any active mode.
                    e.stopPropagation();
                    e.preventDefault();
                    setEditingPrefix(false);
                    setPrefixDraft(initialPrefix);
                  }
                }}
                className="text-xs font-bold text-amber-900 bg-amber-50 border border-amber-300 rounded px-2 py-1 w-44 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                placeholder="Bv. ???, BETALENDE…"
                maxLength={40}
                autoFocus
                disabled={savingPrefix}
              />
              <button type="button" onClick={handleSavePrefix} disabled={savingPrefix}
                className="shrink-0 px-2.5 py-1 text-xs font-bold rounded bg-td-blue text-white hover:bg-td-blue/90 disabled:opacity-50 transition-colors"
              >
                {savingPrefix ? '…' : 'Opslaan'}
              </button>
              <button type="button" onClick={() => { setEditingPrefix(false); setPrefixDraft(initialPrefix); }} disabled={savingPrefix}
                className="shrink-0 px-2.5 py-1 text-xs font-semibold rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Annuleren
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => setEditingPrefix(true)} title="Klik om prefix toe te voegen of te wijzigen"
              className={initialPrefix.trim()
                ? 'shrink-0 text-xs font-bold text-amber-800 bg-amber-100 hover:bg-amber-200 px-1.5 py-0.5 rounded truncate max-w-[50%] transition-colors'
                : 'shrink-0 text-xs font-medium text-amber-700/70 bg-amber-50 hover:bg-amber-100 px-1.5 py-0.5 rounded border border-dashed border-amber-300 transition-colors'
              }
            >
              {initialPrefix.trim() || '+ prefix'}
            </button>
          )}
          <h3 className="text-base font-bold text-td-navy truncate">
            {headlineLabel}
            {soName && <span className="ml-1 font-normal text-td-muted">· {soName}</span>}
          </h3>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {showHold && (
          <button type="button" onClick={() => setOnHold(task.id, !isOnHold)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${isOnHold ? 'text-green-700 border-green-400 hover:bg-green-50' : 'text-amber-700 border-amber-400 hover:bg-amber-50'}`}
          >
            {isOnHold ? 'Heractiveren' : 'On hold'}
          </button>
        )}
        {showMove && (
          <button type="button"
            onClick={() => {
              const fromDate = typeof task.date_deadline === 'string' ? task.date_deadline.slice(0, 10) : '';
              const fromDriverId = task.x_studio_installateur?.[0] ?? 0;
              enterMoveMode(task, fromDate, fromDriverId);
              selectTask(null);
            }}
            className="px-3 py-1.5 text-xs font-semibold text-td-blue border border-td-blue rounded-lg hover:bg-td-blue/5 transition-colors"
          >
            Verplaatsen
          </button>
        )}
        {showMove && (
          <button type="button" onClick={() => setScheduleOtherOpen(true)}
            className="px-3 py-1.5 text-xs font-semibold text-td-blue border border-td-blue rounded-lg hover:bg-td-blue/5 transition-colors"
          >
            Plan op iemand anders
          </button>
        )}
        {scheduleOtherOpen && (
          <EasykitScheduleOtherModal task={task} onClose={() => setScheduleOtherOpen(false)} />
        )}
        {showUnschedule && (
          <button type="button" onClick={() => unscheduleTask(task.id)}
            className="px-3 py-1.5 text-xs font-semibold text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50 transition-colors"
          >
            Verwijder uit planning
          </button>
        )}
        {showDelete && (
          <button type="button" onClick={() => deleteTask(task.id)}
            className="px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
          >
            Verwijderen
          </button>
        )}
        <button type="button" onClick={() => selectTask(null)} aria-label="Sluiten"
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
        >
          <span className="text-lg text-td-navy" aria-hidden="true">&times;</span>
        </button>
      </div>
    </div>
  );
}
